import React from 'react'
import convert from 'htmr'
import withSSR from 'js/components/withSSR/withSSR'
import Spinner from 'js/components/Spinner/Spinner'
import ErrorPage from 'js/pages/ErrorPage'
import { runQuery } from 'js/dato'
import Footer from 'js/components/Footer/Footer'
import { Navigation } from 'js/pages/Home/Navigation'
import { Title } from 'react-head'
import PersonCard, { PersonCardFragment } from './components/PersonCard'
import { AdvisoryBoard_advisoryBoard } from './types/AdvisoryBoard'

type Props = {
  data: AdvisoryBoard_advisoryBoard
  isLoading: boolean
  error: any
}

function AdvisoryBoard({ data, isLoading, error }: Props) {
  if (isLoading)
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Spinner width="50" color="green" />
      </div>
    )

  if (error) {
    return <ErrorPage error={error} />
  }

  const {
    heading,
    introText,
    advisoryBoardHeading,
    advisoryBoardText,
    advisoryBoard,
    nutritionTeamHeading,
    nutritionTeamText,
    nutritionTeam,
  } = data

  if (!advisoryBoardText || !nutritionTeamText) {
    throw new Error(
      `typeguard: required prop was not set advisoryBoardText: ${advisoryBoardText}, nutritionTeamText: ${nutritionTeamText}`,
    )
  }

  return (
    <>
      <Title>{heading} - Lifesum</Title>
      <div className="bg-BgSub py-20 lg:py-40">
        <Navigation theme="sub" minimal={false} />
      </div>
      <div className="bg-BgSub px-16 py-48 text-center md:py-64 lg:py-128">
        <h1 className="type-large-fat mb-12 text-green md:mb-24">{heading}</h1>
      </div>
      <div className="bg-Bg pb-128">
        {introText && (
          <p className="mx-auto max-w-1024 pt-48 text-center leading-body type-17 md:type-24 lg:pt-64 lg:type-28">
            {introText}
          </p>
        )}
        <div className="mx-auto max-w-1440 px-20 lg:px-40">
          <h2 className="type-med-plus pt-48 text-center lg:pt-96">
            {advisoryBoardHeading}
          </h2>
          <div className="s-text mx-auto mb-32 mt-16 max-w-968 text-center lg:mb-40">
            {convert(advisoryBoardText)}
          </div>
          <div className="grid gap-8 md:grid-cols-2 md:gap-4 lg:gap-8 xl:grid-cols-3 xl:gap-10">
            {advisoryBoard.map(({ name, ...person }) => {
              if (!name) {
                throw new Error(
                  `typeguard: required nutritionTeam prop is missing, ${JSON.stringify(
                    person,
                  )}`,
                )
              }

              return <PersonCard key={person.id} name={name} {...person} />
            })}
          </div>
          <h2 className="type-med-plus pt-48 text-center lg:pt-96">
            {nutritionTeamHeading}
          </h2>
          <div className="s-text mx-auto mb-32 mt-16 max-w-968 text-center lg:mb-40">
            {convert(nutritionTeamText)}
          </div>
          <div className="grid gap-8 md:grid-cols-2 md:gap-6 xl:grid-cols-3 xl:gap-10">
            {nutritionTeam.map(({ name, ...person }) => {
              if (!name) {
                throw new Error(
                  `typeguard: required nutritionTeam prop is missing, ${JSON.stringify(
                    person,
                  )}`,
                )
              }

              return <PersonCard key={person.id} name={name} {...person} />
            })}
          </div>
        </div>
      </div>
      <Footer marginless />
    </>
  )
}

const query = gql`
  query AdvisoryBoard {
    advisoryBoard {
      heading
      introText
      advisoryBoardHeading
      advisoryBoardText
      advisoryBoard {
        ...PersonCardFragment
      }
      nutritionTeamHeading
      nutritionTeamText
      nutritionTeam {
        ...PersonCardFragment
      }
    }
  }
  ${PersonCardFragment}
`

AdvisoryBoard.getInitialProps = ({ isPreview }) => {
  return runQuery(query, {}, isPreview).then(
    ({ advisoryBoard }) => advisoryBoard,
  )
}

export default withSSR(AdvisoryBoard)
